import { Clock, Minus, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Slider } from './slider';

// Preset durations in minutes
const DURATION_PRESETS = [
	{ label: '15m', value: 15 },
	{ label: '30m', value: 30 },
	{ label: '1h', value: 60 },
	{ label: '2h', value: 120 },
	{ label: '4h', value: 240 },
	{ label: '8h', value: 480 },
];

/**
 * Format duration in minutes to human-readable string
 */
function formatDuration(minutes) {
	if (minutes < 60) {
		return `${minutes} min`;
	}
	const hours = Math.floor(minutes / 60);
	const mins = minutes % 60;
	if (mins === 0) {
		return `${hours} hour${hours !== 1 ? 's' : ''}`;
	}
	return `${hours}h ${mins}m`;
}

/**
 * DurationPicker - A component for selecting booking duration
 *
 * @param {Object} props
 * @param {number} props.value - Duration in minutes
 * @param {Function} props.onChange - Callback when duration changes
 * @param {number} props.min - Minimum duration in minutes (default: 30)
 * @param {number} props.max - Maximum duration in minutes (default: 1440 / 24 hours)
 * @param {number} props.step - Step increment in minutes (default: 15)
 * @param {boolean} props.showPresets - Show preset duration buttons (default: true)
 */
export function DurationPicker({
	value = 60,
	onChange,
	min = 30,
	max = 1440,
	step = 15,
	showPresets = true,
	className,
}) {
	const handleSliderChange = (values) => {
		onChange(values[0]);
	};

	const handleIncrement = () => {
		const newValue = Math.min(value + step, max);
		onChange(newValue);
	};

	const handleDecrement = () => {
		const newValue = Math.max(value - step, min);
		onChange(newValue);
	};

	const handlePresetClick = (presetValue) => {
		const clampedValue = Math.min(Math.max(presetValue, min), max);
		onChange(clampedValue);
	};

	return (
		<div className={cn('space-y-4', className)}>
			{/* Duration display with +/- controls */}
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2 text-sm text-gray-600">
					<Clock className="h-4 w-4" />
					<span>Duration</span>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={handleDecrement}
						disabled={value <= min}
						className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<Minus className="h-4 w-4" />
					</button>
					<div className="min-w-[80px] text-center">
						<span className="text-lg font-semibold text-gray-900">
							{formatDuration(value)}
						</span>
					</div>
					<button
						type="button"
						onClick={handleIncrement}
						disabled={value >= max}
						className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<Plus className="h-4 w-4" />
					</button>
				</div>
			</div>

			{/* Slider */}
			<div className="px-1">
				<Slider
					value={[value]}
					onValueChange={handleSliderChange}
					min={min}
					max={max}
					step={step}
				/>
				<div className="mt-1 flex justify-between text-xs text-gray-400">
					<span>{formatDuration(min)}</span>
					<span>{formatDuration(max)}</span>
				</div>
			</div>

			{/* Preset buttons */}
			{showPresets && (
				<div className="flex flex-wrap gap-2">
					{DURATION_PRESETS.filter((preset) => preset.value >= min && preset.value <= max).map(
						(preset) => (
							<button
								key={preset.value}
								type="button"
								onClick={() => handlePresetClick(preset.value)}
								className={cn(
									'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
									value === preset.value
										? 'bg-indigo-600 text-white'
										: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
								)}
							>
								{preset.label}
							</button>
						)
					)}
				</div>
			)}
		</div>
	);
}

export default DurationPicker;
